import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { Input } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/customer/auth/AuthContext";

import { useDeleteAccount, useUpdateEmail, useUpdatePassword } from "./api";

// Phase 2a customer-accounts FR-7 + FR-9 + FR-12 + FR-15. Email change,
// password change, and self-service account deletion. Email change is
// here (not on Profile) because it requires the current password.
export function SecurityTab(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const updateEmail = useUpdateEmail();
  const updatePassword = useUpdatePassword();
  const deleteAccount = useDeleteAccount();

  const [emailForm, setEmailForm] = useState({ email: "", current_password: "" });
  const [emailNote, setEmailNote] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwNote, setPwNote] = useState<string | null>(null);
  const [pwError, setPwError] = useState<string | null>(null);

  const [confirmDelete, setConfirmDelete] = useState(false);

  const onEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setEmailError(null);
    setEmailNote(null);
    try {
      await updateEmail.mutateAsync({
        email: emailForm.email.trim().toLowerCase(),
        current_password: emailForm.current_password
      });
      setEmailNote("Check both your old and new inboxes — we sent confirmation to each.");
      setEmailForm({ email: "", current_password: "" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setEmailError("Current password is incorrect.");
      } else if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { errors?: Record<string, string> } | undefined;
        setEmailError(body?.errors?.email ?? "That email isn't allowed.");
      } else {
        setEmailError("Couldn't change your email. Try again.");
      }
    }
  };

  const onPasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPwError(null);
    setPwNote(null);
    if (pwForm.next !== pwForm.confirm) {
      setPwError("New password and confirmation don't match.");
      return;
    }
    try {
      await updatePassword.mutateAsync({
        current_password: pwForm.current,
        password: pwForm.next,
        password_confirmation: pwForm.confirm
      });
      setPwNote("Password updated. Other devices will need to sign in again.");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setPwError("Current password is incorrect.");
      } else if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { errors?: Record<string, string> } | undefined;
        setPwError(body?.errors?.password ?? "Password doesn't meet requirements.");
      } else {
        setPwError("Couldn't change your password. Try again.");
      }
    }
  };

  const onDelete = async () => {
    try {
      await deleteAccount.mutateAsync();
      auth.setUser(null);
      navigate("/?account=deleted", { replace: true });
    } catch {
      window.alert("We couldn't delete your account. Please contact support.");
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="space-y-3 rounded-md border border-zinc-200 bg-brand-white p-4">
        <h2 className="text-sm font-semibold">Change email</h2>
        <form onSubmit={onEmailSubmit} className="space-y-3" noValidate>
          {emailError ? <Alert variant="destructive" role="alert">{emailError}</Alert> : null}
          {emailNote ? <Alert variant="success" role="status">{emailNote}</Alert> : null}
          <FormField id="new-email" label="New email" required>
            <Input id="new-email" type="email" autoComplete="email"
                   value={emailForm.email}
                   onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })} required />
          </FormField>
          <FormField id="email-pw" label="Current password" required>
            <Input id="email-pw" type="password" autoComplete="current-password"
                   value={emailForm.current_password}
                   onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} required />
          </FormField>
          <Button type="submit" disabled={updateEmail.isPending}>
            {updateEmail.isPending ? "Saving…" : "Change email"}
          </Button>
        </form>
      </section>

      <section className="space-y-3 rounded-md border border-zinc-200 bg-brand-white p-4">
        <h2 className="text-sm font-semibold">Change password</h2>
        <form onSubmit={onPasswordSubmit} className="space-y-3" noValidate>
          {pwError ? <Alert variant="destructive" role="alert">{pwError}</Alert> : null}
          {pwNote ? <Alert variant="success" role="status">{pwNote}</Alert> : null}
          <FormField id="cur-pw" label="Current password" required>
            <Input id="cur-pw" type="password" autoComplete="current-password"
                   value={pwForm.current}
                   onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} required />
          </FormField>
          <FormField id="new-pw" label="New password" required hint="At least 10 characters, with a letter and a number.">
            <Input id="new-pw" type="password" autoComplete="new-password"
                   value={pwForm.next}
                   onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} required />
          </FormField>
          <FormField id="confirm-pw" label="Confirm new password" required>
            <Input id="confirm-pw" type="password" autoComplete="new-password"
                   value={pwForm.confirm}
                   onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} required />
          </FormField>
          <Button type="submit" disabled={updatePassword.isPending}>
            {updatePassword.isPending ? "Saving…" : "Change password"}
          </Button>
        </form>
      </section>

      <section className="space-y-3 rounded-md border border-danger/40 bg-red-50/30 p-4 lg:col-span-2">
        <h2 className="text-sm font-semibold text-danger">Delete your account</h2>
        <p className="text-sm text-zinc-700">
          This anonymizes your profile and addresses. Past orders stay in our records for tax and legal compliance,
          but your personal information will be removed and you&rsquo;ll be signed out.
        </p>
        {confirmDelete ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-danger">This cannot be undone. Are you sure?</p>
            <div className="flex gap-2">
              <Button type="button" variant="destructive" disabled={deleteAccount.isPending} onClick={onDelete}>
                {deleteAccount.isPending ? "Deleting…" : "Yes, delete my account"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setConfirmDelete(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button type="button" variant="destructive" onClick={() => setConfirmDelete(true)}>
            Delete my account
          </Button>
        )}
      </section>
    </div>
  );
}
